import "./index.css";
import {
  initBv,
  bvApi,
  type BvSession,
  mountShell,
  statRow,
  dataTable,
  card,
  emptyState,
  pill,
  openModal,
  flash,
  fmtMoney,
  fmtDate,
  skeletonCard,
  h,
} from "./bv-init";

interface Stats {
  open: number;
  unlocked: number;
  deals: number;
  participants: number;
}
interface Deal {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  target_count: number;
  unit_price: number;
  list_price: number;
  currency: string;
  status: "open" | "unlocked" | "closed";
  joined: number;
  link: string;
}
interface Participant {
  id: number;
  name: string | null;
  contact: string | null;
  source: string;
  created_at: string;
}
interface Recent {
  name: string | null;
  source: string;
  created_at: string;
  deal: string;
}

let session: BvSession;
let currency = "JMD";

const STATUS_TONE: Record<string, string> = { open: "accent", unlocked: "ok", closed: "" };

boot();

async function boot() {
  try {
    session = await initBv();
  } catch (err) {
    renderFatal(err);
    return;
  }
  currency = session.merchant.currency_code || "JMD";
  mountShell({
    brandIcon: "users",
    brandLogo: "/logo.svg",
    title: "Group Buying",
    subtitle: `${session.merchant.name || "Your store"} · unlock deals together`,
    poweredBy: "Marketplace",
    tabs: [
      { id: "overview", label: "Overview", icon: "users", render: renderOverview },
      { id: "deals", label: "Deals", icon: "tag", render: renderDeals },
    ],
  });
}

function renderFatal(err: unknown) {
  const root = document.getElementById("root")!;
  root.innerHTML = "";
  root.append(
    h(
      "div",
      { class: "bv-fatal" },
      h("strong", null, "Couldn't start"),
      h("p", { class: "bv-muted" }, (err as any)?.message || "No session token found."),
    ),
  );
}

function progressBar(joined: number, target: number): HTMLElement {
  const pct = target > 0 ? Math.min(100, Math.round((joined / target) * 100)) : 0;
  const track = h("div", { style: { background: "var(--line)", borderRadius: "999px", height: "10px", overflow: "hidden", margin: "8px 0 4px" } });
  track.append(h("div", { style: { width: `${pct}%`, height: "100%", background: "var(--accent)" } }));
  return track;
}

async function renderOverview(host: HTMLElement) {
  host.innerHTML = "";
  host.append(skeletonCard());
  const data = await bvApi<{ stats: Stats; recent: Recent[] }>("/api/overview").catch(() => null);
  host.innerHTML = "";
  if (!data) {
    host.append(emptyState({ icon: "alert", title: "Couldn't load", text: "Please try again." }));
    return;
  }
  const s = data.stats;
  host.append(
    statRow([
      { k: "Open deals", v: String(s.open), icon: "tag", tone: Number(s.open) > 0 ? "accent" : undefined },
      { k: "Unlocked", v: String(s.unlocked), icon: "check", tone: "ok" },
      { k: "Participants", v: String(s.participants), icon: "users" },
      { k: "Deals", v: String(s.deals), icon: "list" },
    ]),
  );

  host.append(
    card({
      title: "Start a group deal",
      action: h("button", { class: "primary", onClick: () => openNew(host) }, "New deal"),
      body: h("p", { class: "bv-muted" }, "Set a group price and a target number of buyers. Share the link, or sweep in past customers — the deal unlocks when the target is reached."),
    }),
  );

  host.append(
    card({
      title: "Recent joins",
      body: data.recent.length
        ? dataTable<Recent>({
            columns: [
              { head: "When", cell: (r) => fmtDate(r.created_at) },
              { head: "Name", cell: (r) => r.name || "Anonymous" },
              { head: "Deal", cell: (r) => r.deal },
              { head: "Source", cell: (r) => pill(r.source, r.source === "order" ? "" : "ok") },
            ],
            rows: data.recent,
          })
        : emptyState({ icon: "users", title: "No participants yet", text: "Create a deal and share the link." }),
    }),
  );
}

async function renderDeals(host: HTMLElement) {
  host.innerHTML = "";
  const head = h(
    "div",
    { class: "bv-row", style: { justifyContent: "space-between", alignItems: "center", marginBottom: "12px" } },
    h("h2", { style: { margin: "0", fontSize: "1.05rem" } }, "Deals"),
    h("button", { class: "primary", onClick: () => openNew(host) }, "New deal"),
  );
  const grid = h("div", { class: "bv-grid" });
  host.append(head, grid);
  grid.append(skeletonCard());
  const d = await bvApi<{ deals: Deal[] }>("/api/deals").catch(() => ({ deals: [] as Deal[] }));
  grid.innerHTML = "";
  if (!d.deals.length) {
    grid.append(emptyState({ icon: "tag", title: "No deals yet", text: "Create your first group deal." }));
    return;
  }
  for (const c of d.deals) {
    const body = h("div");
    body.append(
      h(
        "div",
        { class: "bv-row", style: { justifyContent: "space-between", alignItems: "center" } },
        h("strong", null, c.title),
        pill(c.status, STATUS_TONE[c.status] || ""),
      ),
      h(
        "div",
        { style: { marginTop: "8px" } },
        h("span", { style: { fontSize: "1.3rem", fontWeight: "700" } }, fmtMoney(Number(c.unit_price), c.currency || currency)),
        Number(c.list_price) > Number(c.unit_price)
          ? h("span", { class: "bv-muted", style: { textDecoration: "line-through", marginLeft: "8px" } }, fmtMoney(Number(c.list_price), c.currency || currency))
          : null,
      ),
      progressBar(Number(c.joined), Number(c.target_count)),
      h("div", { class: "bv-muted", style: { fontSize: "0.82rem" } }, `${c.joined} of ${c.target_count} joined`),
      h(
        "div",
        { class: "bv-row", style: { marginTop: "12px" } },
        h("button", { class: "secondary", onClick: () => openDeal(c.id, host) }, "Manage"),
      ),
    );
    grid.append(card({ body }));
  }
}

function openNew(host: HTMLElement) {
  const title = h("input", { type: "text", placeholder: "e.g. Bulk rice — 25lb bag" }) as HTMLInputElement;
  const target = h("input", { type: "number", min: "2", step: "1", value: "10" }) as HTMLInputElement;
  const unit = h("input", { type: "number", min: "0", step: "0.01", value: "0" }) as HTMLInputElement;
  const list = h("input", { type: "number", min: "0", step: "0.01", value: "0" }) as HTMLInputElement;
  const desc = h("input", { type: "text", placeholder: "Short description (optional)" }) as HTMLInputElement;
  let close = () => {};
  const handle = openModal({
    title: "New group deal",
    body: h(
      "div",
      { class: "bv-stack" },
      field("Title", title),
      field("Target buyers", target),
      field(`Group price (${currency})`, unit),
      field(`List price (${currency}) — optional`, list),
      field("Description", desc),
    ),
    actions: [
      { label: "Cancel" },
      {
        label: "Create",
        primary: true,
        onClick: () => {
          if (!title.value.trim()) {
            flash("Title is required", "error");
            return true;
          }
          bvApi<{ deal: Deal }>("/api/deals", {
            method: "POST",
            body: JSON.stringify({
              title: title.value.trim(),
              target_count: +target.value,
              unit_price: +unit.value,
              list_price: +list.value,
              description: desc.value.trim() || null,
            }),
          })
            .then((r) => {
              close();
              flash("Deal created", "success");
              openDeal(r.deal.id, host);
            })
            .catch((e) => flash(e?.message || "Failed", "error"));
          return true;
        },
      },
    ],
  });
  close = handle.close;
}

async function openDeal(id: number, host: HTMLElement) {
  const data = await bvApi<{ deal: Deal; participants: Participant[]; joined: number }>(`/api/deals/${id}`).catch(() => null);
  if (!data) return flash("Couldn't load deal", "error");
  const c = data.deal;
  const body = h("div", { class: "bv-stack" });
  body.append(
    h(
      "div",
      { class: "bv-row", style: { justifyContent: "space-between", alignItems: "center" } },
      h("strong", null, c.title),
      pill(c.status, STATUS_TONE[c.status] || ""),
    ),
    h(
      "div",
      null,
      h("span", { style: { fontSize: "1.4rem", fontWeight: "700" } }, fmtMoney(Number(c.unit_price), c.currency || currency)),
      Number(c.list_price) > Number(c.unit_price)
        ? h("span", { class: "bv-muted", style: { textDecoration: "line-through", marginLeft: "8px" } }, fmtMoney(Number(c.list_price), c.currency || currency))
        : null,
    ),
    progressBar(Number(data.joined), Number(c.target_count)),
    h("div", { class: "bv-muted" }, `${data.joined} of ${c.target_count} joined${c.status === "unlocked" ? " · unlocked 🎉" : ""}`),
  );

  let close = () => {};
  const reopen = () => {
    close();
    openDeal(id, host);
  };
  const actions = h("div", { class: "bv-row", style: { flexWrap: "wrap", gap: "8px" } });
  actions.append(
    h("button", { class: "secondary", onClick: () => sweep(id, reopen) }, "Sweep past customers"),
    h("button", { class: "ghost", onClick: () => copyLink(c.link) }, "Copy link"),
  );
  if (c.status !== "closed") actions.append(h("button", { class: "danger", onClick: () => closeDeal(id, reopen) }, "Close"));
  body.append(actions);

  body.append(
    data.participants.length
      ? dataTable<Participant>({
          columns: [
            { head: "When", cell: (r) => fmtDate(r.created_at) },
            { head: "Name", cell: (r) => r.name || "Anonymous" },
            { head: "Contact", cell: (r) => r.contact || "—" },
            { head: "Source", cell: (r) => pill(r.source, r.source === "order" ? "" : "ok") },
          ],
          rows: data.participants,
        })
      : emptyState({ icon: "users", title: "No participants yet", text: "Sweep past customers or share the public link." }),
  );

  const handle = openModal({ title: "Group deal", body, actions: [{ label: "Done" }] });
  close = handle.close;
}

async function sweep(id: number, reopen: () => void) {
  const r = await bvApi<{ added: number; status: string }>(`/api/deals/${id}/sync`, { method: "POST" }).catch(() => null);
  if (!r) return flash("Sweep failed", "error");
  flash(r.added ? `Added ${r.added} participant${r.added === 1 ? "" : "s"} from past customers.${r.status === "unlocked" ? " Deal unlocked! 🎉" : ""}` : "No new customers to add.", r.added ? "success" : "info");
  if (r.added) reopen();
}

async function closeDeal(id: number, reopen: () => void) {
  const ok = await bvApi(`/api/deals/${id}/close`, { method: "POST" }).then(() => true).catch(() => false);
  flash(ok ? "Deal closed" : "Couldn't close", ok ? "success" : "error");
  if (ok) reopen();
}

function copyLink(link: string) {
  navigator.clipboard?.writeText(link).then(
    () => flash("Public link copied", "success"),
    () => flash(link, "info"),
  );
}

function field(label: string, input: HTMLElement): HTMLElement {
  return h("div", { class: "bv-field" }, h("label", { class: "bv-label" }, label), input);
}
